import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function resolveLocale(localeOverride?: string): string {
  // Prefer explicit override if provided (e.g. 'ar' | 'en' | 'ku' or full ICU code)
  let lang = localeOverride;

  if (!lang) {
    if (typeof document !== 'undefined' && document.documentElement.lang) {
      lang = document.documentElement.lang;
    } else if (typeof navigator !== 'undefined' && navigator.language) {
      lang = navigator.language;
    } else {
      lang = 'ar';
    }
  }

  // Normalize to base language code
  const lower = lang.toLowerCase();
  if (lower.startsWith('ar')) return 'ar-IQ';
  if (lower.startsWith('en')) return 'en-US';
  if (lower.startsWith('ku') || lower.startsWith('ckb')) return 'ckb-IQ';

  // Fallback: use whatever was provided
  return lang;
}

export function formatDate(date: string | Date, locale?: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const resolved = resolveLocale(locale);
  return new Intl.DateTimeFormat(resolved, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(d);
}

export function formatDateTime(date: string | Date, locale?: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const resolved = resolveLocale(locale);
  return new Intl.DateTimeFormat(resolved, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export function generateCaseNumber(): string {
  const prefix = 'MOYS';
  const year = new Date().getFullYear();
  const random = Math.floor(Math.random() * 10000)
    .toString()
    .padStart(4, '0');
  return `${prefix}-${year}-${random}`;
}

export function slugify(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-');
}

export function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return `${text.substring(0, length)}...`;
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

export function isRTL(lang: string): boolean {
  return ['ar', 'ku'].includes(lang);
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function downloadFile(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
}

export function validateEmail(email: string): boolean {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

export function validatePhone(phone: string): boolean {
  // Iraqi phone number format: +964XXXXXXXXXX or 07XXXXXXXXX
  const re = /^(\+964|0)?7[3-9]\d{8}$/;
  return re.test(phone.replace(/\s/g, ''));
}

export function validateNationalId(id: string): boolean {
  // Iraqi national ID validation (simplified)
  return /^\d{12}$/.test(id);
}

export function sanitizeHTML(html: string): string {
  // Simple HTML sanitization - in production, use DOMPurify
  const temp = document.createElement('div');
  temp.textContent = html;
  return temp.innerHTML;
}

export function calculateReadTime(text: string, wordsPerMinute = 200): number {
  const words = text.trim().split(/\s+/).length;
  return Math.ceil(words / wordsPerMinute);
}







